import { Card } from '@mui/material';
import { styled } from '@mui/material/styles';

/**
 * Width bands the card lays itself out against.
 *
 * These are container widths, not viewport widths. The same card renders in a
 * one-up mobile list, a two-up records grid and a three-up dashboard row, so
 * the viewport says nothing useful about how much room it actually has.
 */
const STACKED = '380px';
const WIDE = '560px';

/**
 * These use MUI's `styled` rather than styled-components like the rest of the
 * repo. styled-components 5 compiles through stylis 3, which does not know
 * `@container` and drops the selector when it nests one, so the rules inside
 * silently never apply. MUI's emotion is on stylis 4 and handles it.
 */

/**
 * Establishes the query container.
 *
 * Has to be a separate element from the card: an element cannot both define a
 * container and read its own container queries.
 */
export const CardContainer = styled('div')({
  containerType: 'inline-size',
  height: '100%',
});

export const CardRoot = styled(Card)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5),
  cursor: 'pointer',
  transition: 'box-shadow .12s linear, border-color .12s linear',

  '&:hover': {
    borderColor: theme.palette.primary.main,
    boxShadow: theme.shadows[3],
  },

  [`@container (min-width: ${STACKED})`]: {
    gap: theme.spacing(1.75),
    padding: theme.spacing(1.75, 2),
  },
}));

/**
 * Fixed-width icon column.
 *
 * The old card gave this 30% of the card, so a wide card spent 200px on a
 * glyph while the text it was labelling stayed clumped in the middle.
 */
export const IconWrap = styled('div')({
  flex: '0 0 auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 32,
  fontSize: 26,

  [`@container (min-width: ${STACKED})`]: {
    width: 38,
    fontSize: 30,
  },
});

/**
 * Value and text, stacked until the card is wide enough to sit them side by
 * side. The value column is fixed so labels of different lengths still line
 * their text up against each other.
 */
export const Body = styled('div')(({ theme }) => ({
  flex: '1 1 auto',
  minWidth: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.375),

  [`@container (min-width: ${WIDE})`]: {
    display: 'grid',
    gridTemplateColumns: '150px minmax(0, 1fr)',
    alignItems: 'center',
    gap: theme.spacing(2.5),
  },
}));

export const ValueRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: theme.spacing(0.75),
  fontSize: 28,

  [`@container (min-width: ${STACKED})`]: {
    fontSize: 32,
  },
}));

export const TextBlock = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.25),
  minWidth: 0,
  flex: '0 0 auto',

  [`@container (min-width: ${WIDE})`]: {
    flex: '1 1 auto',
  },
}));

/**
 * Date and source on one line. The date never wraps and the source ellipses,
 * rather than the source dropping to its own line and colliding with whatever
 * sat under it.
 */
export const SourceLine = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.75),
  minWidth: 0,
}));

export const ChevronCircle = styled('div')(({ theme }) => ({
  flex: '0 0 auto',
  alignSelf: 'center',
  width: 30,
  height: 30,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  backgroundColor: theme.palette.common.accentLightblue,
}));
